import React, { useState } from 'react';

import { timingBridgeAPI } from '../../../lib/api';
import ChampionshipEditor, { cleanChampionships } from './ChampionshipEditor';

const BUTTON = 'px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-30';
const SMALL_FIELD = 'bg-[#0a0a0a] border border-[#2a2a2a] rounded px-1.5 py-1 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

/** Add points for one rider today: a Superpole win, a penalty, a correction. */
function AdjustForm({ busy, onAdd }) {
  const [open, setOpen] = useState(false);
  const [no, setNo] = useState('');
  const [points, setPoints] = useState('2');
  const [reason, setReason] = useState('Superpole');
  if (!open) return <button onClick={() => setOpen(true)} className="text-[11px] text-blue-500 hover:text-blue-400 mt-1">+ Add points</button>;
  const value = Number(points);
  const ready = no.trim() && Number.isFinite(value) && value !== 0 && reason.trim();
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
      <input value={no} onChange={e => setNo(e.target.value)} placeholder="Rider #" className={`${SMALL_FIELD} w-16`} autoFocus />
      <input type="number" step="0.1" value={points} onChange={e => setPoints(e.target.value)} className={`${SMALL_FIELD} w-14`} title="Negative for a penalty" />
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason" className={`${SMALL_FIELD} w-28`} />
      <button
        disabled={busy || !ready}
        onClick={() => onAdd({ no: no.trim(), points: value, reason: reason.trim() }).then(() => { setOpen(false); setNo(''); })}
        className="px-2 py-1 text-[11px] rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-30"
      >
        Add
      </button>
      <button onClick={() => setOpen(false)} className="text-[11px] text-gray-500 hover:text-gray-300">Cancel</button>
    </div>
  );
}

/**
 * Live championship standings on the Live screen, one table per championship
 * (MXGB: 125, MX1, MX2, U21): what the race on track is doing to each, and the
 * day's banked races — with what an operator may need to do by hand: bank the
 * last race of the day, undo a bank, reload the standings, change the list.
 */
export default function ChampionshipPanel({ showId, summary, payload, onChanged }) {
  const champs = summary?.championship || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  async function act(name, fn) {
    setBusy(name);
    setError('');
    try {
      await fn();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Action failed');
    } finally {
      setBusy('');
    }
  }

  function startEditing() {
    setDraft(champs.map(c => ({ label: c.label, url: c.url, match: c.match, score_by: c.scoreBy, system: c.system || 'mxgb', groups: (c.groups || []).join(', '), top_n: c.topN, rounds_remaining: c.roundsRemaining ?? '', motos_per_round: c.motosPerRound || 2 })));
    setEditing(true);
  }

  const streamConfigured = summary?.config?.streams?.championship;
  const onTrack = champs.filter(c => c.live);
  const pending = champs.filter(c => c.pending);

  return (
    <section className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-white">Live championships</h2>
        <button onClick={editing ? () => setEditing(false) : startEditing} className="text-[11px] text-blue-500 hover:text-blue-400">
          {editing ? 'Cancel' : champs.length ? 'Change' : 'Set up'}
        </button>
      </div>

      {error && <div className="text-xs text-red-400">{error}</div>}

      {editing && (
        <div className="space-y-2">
          <ChampionshipEditor value={draft} onChange={setDraft} />
          <button
            onClick={() => act('save', () => timingBridgeAPI.updateConfig(showId, { championships: cleanChampionships(draft) })).then(() => setEditing(false))}
            disabled={busy === 'save'}
            className={BUTTON}
          >
            {busy === 'save' ? 'Loading standings…' : 'Save'}
          </button>
          <p className="text-[10px] text-gray-600">A championship whose URL is unchanged keeps today's banked races; a new or re-pointed one starts afresh.</p>
        </div>
      )}

      {!champs.length && !editing && <p className="text-xs text-gray-500">None set. Add the meeting's championships to publish live standings.</p>}

      {champs.length > 0 && (
        <>
          {!streamConfigured && <div className="text-xs text-amber-400">No championship data stream is set for this series, so these standings are not reaching Singular.</div>}
          <div className={`text-xs ${onTrack.length ? 'text-green-400' : 'text-gray-500'}`}>
            {onTrack.length
              ? `On track now counts for ${onTrack.map(c => c.label).join(' and ')}. The stream's top level shows ${payload?.current || onTrack[0].label}.`
              : `The session on track is not one of these championships' races. The stream's top level shows ${payload?.current || '-'}.`}
            {pending.length > 0 && <span className="text-amber-400"> {pending[0].pending} has finished; it banks when the next session starts.</span>}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => act('bank', () => timingBridgeAPI.bankChampionship(showId))}
              disabled={!!busy || (!onTrack.length && !pending.length)}
              className={BUTTON}
              title="For the last race of the day: banks the race on track now, into every championship it counts for. Other races bank themselves when the next session starts."
            >
              Bank this race now
            </button>
            <button onClick={() => act('reload', () => timingBridgeAPI.reloadChampionship(showId))} disabled={!!busy} className={BUTTON}>
              {busy === 'reload' ? 'Reloading…' : 'Reload standings'}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {champs.map(c => {
              const table = payload?.all?.[c.key];
              return (
                <div key={c.key} className={`rounded border px-3 py-2 ${c.live ? 'border-green-800 bg-green-950/10' : 'border-[#2a2a2a]'}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-white">{c.label}</span>
                    {c.live && <span className="text-[10px] text-green-400">LIVE</span>}
                    {c.scoreBy === 'within' && <span className="text-[10px] text-gray-500">within class</span>}
                    {c.system === 'bssc' && <span className="text-[10px] text-gray-500">per day</span>}
                    <span className="ml-auto text-[10px] text-gray-600 truncate" title={c.name || ''}>{c.name || (c.loading ? 'loading…' : '')}</span>
                  </div>
                  {c.error && <div className="text-[11px] text-red-400 mt-1">{c.error}</div>}
                  {c.suggestion && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded border border-amber-700/60 bg-amber-950/20 px-2 py-1.5 text-[11px] text-amber-300">
                      <span>{c.suggestion.reason}: #{c.suggestion.no} {c.suggestion.name} won {c.suggestion.session}. Add +{c.suggestion.points} to {c.label}?</span>
                      <span className="ml-auto flex gap-1.5">
                        <button onClick={() => act(`suggest-${c.key}`, () => timingBridgeAPI.answerChampionshipSuggestion(showId, c.key, true))} disabled={!!busy} className="px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-500 text-black font-semibold disabled:opacity-30">Add +{c.suggestion.points}</button>
                        <button onClick={() => act(`suggest-${c.key}`, () => timingBridgeAPI.answerChampionshipSuggestion(showId, c.key, false))} disabled={!!busy} className="px-2 py-0.5 rounded border border-amber-700/60 hover:text-white disabled:opacity-30">No</button>
                      </span>
                    </div>
                  )}
                  {table?.rows?.length > 0 && (
                    <table className="w-full text-xs mt-1">
                      <tbody>
                        {table.rows.map(r => (
                          <tr key={r.no} className="border-t border-[#2a2a2a]">
                            <td className="py-0.5 w-10 text-white tabular-nums">
                              {r.pos}{r.tied && '='}
                              {r.change !== 0 && <span className={`ml-1 text-[9px] ${r.change > 0 ? 'text-green-400' : 'text-red-400'}`}>{r.change > 0 ? '▲' : '▼'}{Math.abs(r.change)}</span>}
                            </td>
                            <td className="py-0.5 text-gray-200 truncate"><span className="text-gray-500 tabular-nums mr-1">#{r.no}</span>{r.short}</td>
                            <td className="py-0.5 text-right text-white tabular-nums">{r.points}</td>
                            <td className="py-0.5 text-right text-gray-500 tabular-nums w-12">{r.gap}</td>
                            <td className="py-0.5 text-right text-gray-500 tabular-nums w-16">{r.inRace ? `P${r.racePos} ${r.racePointsText}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {c.adjustments?.length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {c.adjustments.map(a => (
                        <div key={a.id} className="flex items-center gap-2 text-[11px] text-gray-400">
                          <span className={a.points > 0 ? 'text-green-400' : 'text-red-400'}>#{a.no} {a.points > 0 ? '+' : ''}{a.points}</span>
                          <span>{a.reason}</span>
                          <span className="text-gray-600">{new Date(a.at).toLocaleTimeString()}</span>
                          <button onClick={() => act(`unadjust-${c.key}-${a.id}`, () => timingBridgeAPI.removeChampionshipAdjustment(showId, c.key, a.id))} disabled={!!busy} className="ml-auto text-gray-500 hover:text-red-400">Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <AdjustForm busy={!!busy} onAdd={(adj) => act(`adjust-${c.key}`, () => timingBridgeAPI.adjustChampionship(showId, c.key, adj))} />
                  {c.banked?.length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {c.banked.map(b => (
                        <div key={b.id} className="flex items-center gap-2 text-[11px] text-gray-500">
                          <span>Banked {b.label}</span>
                          <span className="text-gray-600">{b.by === 'auto' ? 'auto' : 'by hand'} · {new Date(b.at).toLocaleTimeString()}</span>
                          <button onClick={() => act(`unbank-${c.key}-${b.id}`, () => timingBridgeAPI.unbankChampionship(showId, c.key, b.id))} disabled={!!busy} className="ml-auto hover:text-red-400">Undo</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {champs.some(c => c.banked?.length || c.adjustments?.length) && (
            <button
              onClick={() => {
                if (window.confirm('Reload every championship and forget today\'s banked races and points adjustments? Only do this once the sources already include today\'s results.')) {
                  act('reload', () => timingBridgeAPI.reloadChampionship(showId, true));
                }
              }}
              disabled={!!busy}
              className="text-[11px] text-gray-500 hover:text-gray-300"
            >
              Reload, the sources now include today's races
            </button>
          )}
        </>
      )}
    </section>
  );
}
